import React, { Component } from 'react';
import { Card, Row, Col, Statistic, Progress, Alert, Button, Space, Tag, Timeline, Table, Badge, Tooltip, Switch } from 'antd';
import {
	ServerOutlined,
	CloudOutlined,
	WifiOutlined,
	DatabaseOutlined,
	WarningOutlined,
	CheckCircleOutlined,
	ExclamationCircleOutlined,
	ReloadOutlined,
	SettingOutlined,
	MonitorOutlined,
	GlobalOutlined,
	ThunderboltOutlined,
	HddOutlined,
	ApiOutlined,
	SafetyOutlined,
	ClockCircleOutlined,
	FireOutlined,
	LineChartOutlined
} from '@ant-design/icons';
import './SystemStatus.scss';

class SystemStatus extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			refreshing: false,
			autoRefresh: true,
			
			// System metrics
			systemMetrics: {
				cpu: 0,
				memory: 0,
				disk: 0,
				network: 0
			},
			
			// Server details
			serverDetails: [],
			
			// CDN status
			cdnStatus: {
				nodes: [],
				totalBandwidth: 0,
				cacheHitRate: 0,
				avgLatency: 0
			},
			
			// Service status
			serviceStatus: {
				streaming: 'operational',
				database: 'operational',
				cdn: 'operational',
				api: 'operational',
				auth: 'operational',
				storage: 'operational'
			},
			
			// Performance metrics
			performanceMetrics: {
				responseTime: 0,
				throughput: 0,
				errorRate: 0,
				uptime: 0
			},
			
			// Recent incidents
			recentIncidents: []
		};
	}

	componentDidMount() {
		this.loadSystemStatus();
		this.startAutoRefresh();
	}

	componentWillUnmount() {
		this.stopAutoRefresh();
	}

	// Load system status data
	loadSystemStatus = () => {
		this.setState({ loading: true });
		
		Promise.all([
			this.loadSystemMetrics(),
			this.loadServerDetails(),
			this.loadCDNStatus(),
			this.loadServiceStatus(),
			this.loadPerformanceMetrics(),
			this.loadRecentIncidents()
		]).finally(() => {
			this.setState({ loading: false });
		});
	}

	// Start auto refresh
	startAutoRefresh = () => {
		if (this.state.autoRefresh) {
			this.refreshInterval = setInterval(() => {
				this.loadSystemStatus();
			}, 30000); // Refresh every 30 seconds
		}
	}

	// Stop auto refresh
	stopAutoRefresh = () => {
		if (this.refreshInterval) {
			clearInterval(this.refreshInterval);
		}
	}

	// Load system metrics
	loadSystemMetrics = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				this.setState({
					systemMetrics: {
						cpu: Math.floor(Math.random() * 30) + 40, // 40-70%
						memory: Math.floor(Math.random() * 25) + 60, // 60-85%
						disk: Math.floor(Math.random() * 20) + 45, // 45-65%
						network: Math.floor(Math.random() * 40) + 30 // 30-70%
					}
				});
				resolve();
			}, 200);
		});
	}

	// Load server details
	loadServerDetails = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				const servers = Array.from({ length: 12 }, (_, i) => ({
					id: `server-${i + 1}`,
					name: `Stream Server ${i + 1}`,
					location: ['US-East', 'US-West', 'EU-Central', 'Asia-Pacific'][Math.floor(Math.random() * 4)],
					status: Math.random() > 0.1 ? 'online' : 'offline',
					cpu: Math.floor(Math.random() * 40) + 30,
					memory: Math.floor(Math.random() * 30) + 50,
					activeStreams: Math.floor(Math.random() * 50) + 10,
					bandwidth: Math.floor(Math.random() * 500) + 100,
					uptime: Math.floor(Math.random() * 30) + 1
				}));
				
				this.setState({ serverDetails: servers });
				resolve();
			}, 300);
		});
	}

	// Load CDN status
	loadCDNStatus = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				const nodes = Array.from({ length: 8 }, (_, i) => ({
					id: `cdn-${i + 1}`,
					location: ['New York', 'Los Angeles', 'London', 'Frankfurt', 'Tokyo', 'Sydney', 'São Paulo', 'Mumbai'][i],
					status: Math.random() > 0.05 ? 'healthy' : 'degraded',
					latency: Math.floor(Math.random() * 50) + 20,
					bandwidth: Math.floor(Math.random() * 200) + 50,
					cacheHitRate: Math.floor(Math.random() * 20) + 80
				}));

				this.setState({
					cdnStatus: {
						nodes,
						totalBandwidth: nodes.reduce((sum, node) => sum + node.bandwidth, 0),
						cacheHitRate: nodes.reduce((sum, node) => sum + node.cacheHitRate, 0) / nodes.length,
						avgLatency: nodes.reduce((sum, node) => sum + node.latency, 0) / nodes.length
					}
				});
				resolve();
			}, 250);
		});
	}

	// Load service status
	loadServiceStatus = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				const services = ['streaming', 'database', 'cdn', 'api', 'auth', 'storage'];
				const statuses = {};
				
				services.forEach(service => {
					const rand = Math.random();
					if (rand > 0.95) statuses[service] = 'down';
					else if (rand > 0.85) statuses[service] = 'degraded';
					else statuses[service] = 'operational';
				});

				this.setState({ serviceStatus: statuses });
				resolve();
			}, 150);
		});
	}

	// Load performance metrics
	loadPerformanceMetrics = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				this.setState({
					performanceMetrics: {
						responseTime: Math.floor(Math.random() * 50) + 120, // 120-170ms
						throughput: Math.floor(Math.random() * 500) + 1000, // 1000-1500 req/s
						errorRate: Math.random() * 0.5, // 0-0.5%
						uptime: 99.8 + Math.random() * 0.19 // 99.8-99.99%
					}
				});
				resolve();
			}, 100);
		});
	}

	// Load recent incidents
	loadRecentIncidents = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				const incidents = [
					{
						id: 1,
						title: 'High server load on US-East region',
						severity: 'warning',
						status: 'investigating',
						timestamp: new Date(Date.now() - 1800000).toISOString(), // 30 min ago
						description: 'Increased CPU usage detected on streaming servers'
					},
					{
						id: 2,
						title: 'CDN cache refresh completed',
						severity: 'info',
						status: 'resolved',
						timestamp: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
						description: 'Scheduled maintenance completed successfully'
					},
					{
						id: 3,
						title: 'Database connection timeout',
						severity: 'critical',
						status: 'resolved',
						timestamp: new Date(Date.now() - 7200000).toISOString(), // 2 hours ago
						description: 'Brief connection issues resolved, no data loss'
					}
				];

				this.setState({ recentIncidents: incidents });
				resolve();
			}, 180);
		});
	}

	// Handle refresh
	handleRefresh = () => {
		this.setState({ refreshing: true });
		this.loadSystemStatus().finally(() => {
			this.setState({ refreshing: false });
		});
		this.props.onRefresh && this.props.onRefresh();
	}

	// Handle auto refresh toggle
	handleAutoRefreshToggle = (checked) => {
		this.setState({ autoRefresh: checked });
		if (checked) {
			this.startAutoRefresh();
		} else {
			this.stopAutoRefresh();
		}
	}

	// Get status color
	getStatusColor = (status) => {
		const colors = {
			operational: '#22c55e',
			healthy: '#22c55e',
			online: '#22c55e',
			degraded: '#f59e0b',
			warning: '#f59e0b',
			down: '#ef4444',
			offline: '#ef4444',
			critical: '#ef4444'
		};
		return colors[status] || '#6b7280';
	}

	// Get status icon
	getStatusIcon = (status) => {
		const icons = {
			operational: <CheckCircleOutlined />,
			healthy: <CheckCircleOutlined />,
			online: <CheckCircleOutlined />,
			degraded: <WarningOutlined />,
			warning: <WarningOutlined />,
			down: <ExclamationCircleOutlined />,
			offline: <ExclamationCircleOutlined />,
			critical: <ExclamationCircleOutlined />
		};
		return icons[status] || <CheckCircleOutlined />;
	}

	// Get metric color based on value
	getMetricColor = (value, thresholds) => {
		if (value >= thresholds.critical) return '#ef4444';
		if (value >= thresholds.warning) return '#f59e0b';
		return '#22c55e';
	}

	// Render system overview
	renderSystemOverview = () => {
		const { systemMetrics, performanceMetrics } = this.state;

		return (
			<Row gutter={[16, 16]} className="system-overview">
				<Col span={6}>
					<Card size="small">
						<Statistic
							title="CPU Usage"
							value={systemMetrics.cpu}
							suffix="%"
							valueStyle={{ 
								color: this.getMetricColor(systemMetrics.cpu, { warning: 70, critical: 85 })
							}}
						/>
						<Progress
							percent={systemMetrics.cpu}
							strokeColor={this.getMetricColor(systemMetrics.cpu, { warning: 70, critical: 85 })}
							showInfo={false}
							size="small"
						/>
					</Card>
				</Col>
				<Col span={6}>
					<Card size="small">
						<Statistic
							title="Memory Usage"
							value={systemMetrics.memory}
							suffix="%"
							valueStyle={{ 
								color: this.getMetricColor(systemMetrics.memory, { warning: 80, critical: 90 })
							}}
						/>
						<Progress
							percent={systemMetrics.memory}
							strokeColor={this.getMetricColor(systemMetrics.memory, { warning: 80, critical: 90 })}
							showInfo={false}
							size="small"
						/>
					</Card>
				</Col>
				<Col span={6}>
					<Card size="small">
						<Statistic
							title="Disk Usage"
							value={systemMetrics.disk}
							suffix="%"
							valueStyle={{ 
								color: this.getMetricColor(systemMetrics.disk, { warning: 75, critical: 90 })
							}}
						/>
						<Progress
							percent={systemMetrics.disk}
							strokeColor={this.getMetricColor(systemMetrics.disk, { warning: 75, critical: 90 })}
							showInfo={false}
							size="small"
						/>
					</Card>
				</Col>
				<Col span={6}>
					<Card size="small">
						<Statistic
							title="Network I/O"
							value={systemMetrics.network}
							suffix="%"
							valueStyle={{ 
								color: this.getMetricColor(systemMetrics.network, { warning: 80, critical: 95 })
							}}
						/>
						<Progress
							percent={systemMetrics.network}
							strokeColor={this.getMetricColor(systemMetrics.network, { warning: 80, critical: 95 })}
							showInfo={false}
							size="small"
						/>
					</Card>
				</Col>
			</Row>
		);
	}

	// Render service status
	renderServiceStatus = () => {
		const { serviceStatus } = this.state;

		const services = [
			{ key: 'streaming', name: 'Streaming Service', icon: <MonitorOutlined /> },
			{ key: 'database', name: 'Database', icon: <DatabaseOutlined /> },
			{ key: 'cdn', name: 'CDN', icon: <GlobalOutlined /> },
			{ key: 'api', name: 'API Gateway', icon: <ApiOutlined /> },
			{ key: 'auth', name: 'Authentication', icon: <SafetyOutlined /> },
			{ key: 'storage', name: 'File Storage', icon: <HddOutlined /> }
		];

		return (
			<Card title="Service Status" className="service-status-card">
				<Row gutter={[16, 16]}>
					{services.map(service => (
						<Col span={8} key={service.key}>
							<div className="service-item">
								<div className="service-info">
									<span className="service-icon">{service.icon}</span>
									<span className="service-name">{service.name}</span>
								</div>
								<Tag 
									color={this.getStatusColor(serviceStatus[service.key])}
									icon={this.getStatusIcon(serviceStatus[service.key])}
								>
									{serviceStatus[service.key].toUpperCase()}
								</Tag>
							</div>
						</Col>
					))}
				</Row>
			</Card>
		);
	}

	// Render server status table
	renderServerStatus = () => {
		const { serverDetails } = this.state;

		const columns = [
			{
				title: 'Server',
				key: 'server',
				render: (_, record) => (
					<div className="server-info">
						<div className="server-name">{record.name}</div>
						<div className="server-location">{record.location}</div>
					</div>
				)
			},
			{
				title: 'Status',
				dataIndex: 'status',
				key: 'status',
				render: (status) => (
					<Tag color={this.getStatusColor(status)} icon={this.getStatusIcon(status)}>
						{status.toUpperCase()}
					</Tag>
				)
			},
			{
				title: 'CPU',
				dataIndex: 'cpu',
				key: 'cpu',
				render: (cpu) => (
					<div className="metric-cell">
						<span>{cpu}%</span>
						<Progress 
							percent={cpu} 
							strokeColor={this.getMetricColor(cpu, { warning: 70, critical: 85 })}
							showInfo={false}
							size="small"
						/>
					</div>
				)
			},
			{
				title: 'Memory',
				dataIndex: 'memory',
				key: 'memory',
				render: (memory) => (
					<div className="metric-cell">
						<span>{memory}%</span>
						<Progress 
							percent={memory} 
							strokeColor={this.getMetricColor(memory, { warning: 80, critical: 90 })}
							showInfo={false}
							size="small"
						/>
					</div>
				)
			},
			{
				title: 'Active Streams',
				dataIndex: 'activeStreams',
				key: 'activeStreams',
				render: (streams) => (
					<Badge count={streams} style={{ backgroundColor: '#3b82f6' }} />
				)
			},
			{
				title: 'Bandwidth',
				dataIndex: 'bandwidth',
				key: 'bandwidth',
				render: (bandwidth) => `${bandwidth} Mbps`
			},
			{
				title: 'Uptime',
				dataIndex: 'uptime',
				key: 'uptime',
				render: (uptime) => `${uptime} days`
			}
		];

		return (
			<Card title="Server Status" className="server-status-card">
				<Table
					columns={columns}
					dataSource={serverDetails}
					rowKey="id"
					pagination={{ pageSize: 6, showSizeChanger: false }}
					size="small"
				/>
			</Card>
		);
	}

	// Render CDN status
	renderCDNStatus = () => {
		const { cdnStatus } = this.state;

		return (
			<Card title="CDN Status" className="cdn-status-card">
				<Row gutter={[16, 16]} className="cdn-metrics">
					<Col span={8}>
						<Statistic
							title="Total Bandwidth"
							value={cdnStatus.totalBandwidth}
							suffix="Mbps"
							prefix={<ThunderboltOutlined />}
							valueStyle={{ color: '#3b82f6' }}
						/>
					</Col>
					<Col span={8}>
						<Statistic
							title="Cache Hit Rate"
							value={cdnStatus.cacheHitRate}
							suffix="%"
							precision={1}
							prefix={<DatabaseOutlined />}
							valueStyle={{ color: '#22c55e' }}
						/>
					</Col>
					<Col span={8}>
						<Statistic
							title="Avg Latency"
							value={cdnStatus.avgLatency}
							suffix="ms"
							precision={1}
							prefix={<WifiOutlined />}
							valueStyle={{ color: '#f59e0b' }}
						/>
					</Col>
				</Row>

				<div className="cdn-nodes">
					<h5>CDN Nodes</h5>
					<Row gutter={[12, 12]}>
						{cdnStatus.nodes.map(node => (
							<Col span={6} key={node.id}>
								<div className="cdn-node">
									<div className="node-header">
										<span className="node-location">{node.location}</span>
										<Tag 
											size="small" 
											color={this.getStatusColor(node.status)}
										>
											{node.status}
										</Tag>
									</div>
									<div className="node-metrics">
										<div className="node-metric">
											<span>Latency: {node.latency}ms</span>
										</div>
										<div className="node-metric">
											<span>Bandwidth: {node.bandwidth}Mbps</span>
										</div>
									</div>
								</div>
							</Col>
						))}
					</Row>
				</div>
			</Card>
		);
	}

	// Render performance metrics
	renderPerformanceMetrics = () => {
		const { performanceMetrics } = this.state;

		return (
			<Row gutter={[16, 16]} className="performance-metrics">
				<Col span={6}>
					<Card size="small">
						<Statistic
							title="Response Time"
							value={performanceMetrics.responseTime}
							suffix="ms"
							prefix={<ClockCircleOutlined />}
							valueStyle={{ 
								color: this.getMetricColor(performanceMetrics.responseTime, { warning: 200, critical: 500 })
							}}
						/>
					</Card>
				</Col>
				<Col span={6}>
					<Card size="small">
						<Statistic
							title="Throughput"
							value={performanceMetrics.throughput}
							suffix="req/s"
							prefix={<LineChartOutlined />}
							valueStyle={{ color: '#3b82f6' }}
						/>
					</Card>
				</Col>
				<Col span={6}>
					<Card size="small">
						<Statistic
							title="Error Rate"
							value={performanceMetrics.errorRate}
							suffix="%"
							precision={2}
							prefix={<ExclamationCircleOutlined />}
							valueStyle={{ 
								color: this.getMetricColor(performanceMetrics.errorRate, { warning: 1, critical: 5 })
							}}
						/>
					</Card>
				</Col>
				<Col span={6}>
					<Card size="small">
						<Statistic
							title="Uptime"
							value={performanceMetrics.uptime}
							suffix="%"
							precision={2}
							prefix={<CheckCircleOutlined />}
							valueStyle={{ color: '#22c55e' }}
						/>
					</Card>
				</Col>
			</Row>
		);
	}

	// Render recent incidents
	renderRecentIncidents = () => {
		const { recentIncidents } = this.state;

		return (
			<Card title="Recent Incidents" className="incidents-card">
				<Timeline>
					{recentIncidents.map(incident => (
						<Timeline.Item
							key={incident.id}
							color={this.getStatusColor(incident.severity)}
							dot={this.getStatusIcon(incident.severity)}
						>
							<div className="incident-item">
								<div className="incident-header">
									<span className="incident-title">{incident.title}</span>
									<Tag color={this.getStatusColor(incident.status)}>
										{incident.status.toUpperCase()}
									</Tag>
								</div>
								<div className="incident-description">
									{incident.description}
								</div>
								<div className="incident-time">
									{new Date(incident.timestamp).toLocaleString()}
								</div>
							</div>
						</Timeline.Item>
					))}
				</Timeline>
			</Card>
		);
	}

	render() {
		const { loading, refreshing, autoRefresh } = this.state;
		const { status } = this.props;

		// Check for critical alerts
		const criticalAlerts = status?.alerts?.filter(alert => alert.type === 'critical') || [];

		return (
			<div className="system-status">
				{/* Header */}
				<div className="status-header">
					<div className="header-left">
						<h3>System Status</h3>
						<p>Real-time monitoring of all system components</p>
					</div>
					<div className="header-controls">
						<Space>
							<span>Auto Refresh:</span>
							<Switch
								checked={autoRefresh}
								onChange={this.handleAutoRefreshToggle}
								size="small"
							/>
							<Button
								icon={<ReloadOutlined spin={refreshing} />}
								onClick={this.handleRefresh}
								loading={refreshing}
							>
								Refresh
							</Button>
							<Button icon={<SettingOutlined />}>
								Settings
							</Button>
						</Space>
					</div>
				</div>

				{/* Critical Alerts */}
				{criticalAlerts.length > 0 && (
					<Alert
						type="error"
						message="Critical System Alerts"
						description={
							<ul style={{ margin: 0, paddingLeft: 20 }}>
								{criticalAlerts.map((alert, index) => (
									<li key={index}>{alert.message}</li>
								))}
							</ul>
						}
						showIcon
						style={{ marginBottom: 16 }}
					/>
				)}

				{/* System Overview */}
				{this.renderSystemOverview()}

				{/* Performance Metrics */}
				<div style={{ marginTop: 16 }}>
					{this.renderPerformanceMetrics()}
				</div>

				{/* Service Status */}
				<div style={{ marginTop: 16 }}>
					{this.renderServiceStatus()}
				</div>

				{/* Server Status and CDN Status */}
				<Row gutter={[16, 16]} style={{ marginTop: 16 }}>
					<Col span={16}>
						{this.renderServerStatus()}
					</Col>
					<Col span={8}>
						{this.renderRecentIncidents()}
					</Col>
				</Row>

				{/* CDN Status */}
				<div style={{ marginTop: 16 }}>
					{this.renderCDNStatus()}
				</div>
			</div>
		);
	}
}

export default SystemStatus;
